import { CommonModule } from '@angular/common';
import { Component, computed } from '@angular/core';
import { GoogleMap, MapMarker, MapPolygon, MapPolyline } from '@angular/google-maps';
import { FieldType, FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';

export interface MappaPoint {
  lat: number;
  lng: number;
}

export interface MappaPolygon {
  paths:
    | google.maps.MVCArray<google.maps.LatLng>
    | google.maps.MVCArray<google.maps.MVCArray<google.maps.LatLng>>
    | google.maps.LatLng[]
    | google.maps.LatLngLiteral[];
  options?: google.maps.PolygonOptions;
}

export interface MappaPolyline {
  path: google.maps.MVCArray<google.maps.LatLng> | google.maps.LatLngLiteral[];
  options?: google.maps.PolylineOptions;
}

export interface MappaMarker {
  position: MappaPoint;
  options?: google.maps.MarkerOptions;
}

export interface MappaProps extends FormlyFieldProps {
  height?: string;
  width?: string;
  zoom?: number;
  center?: MappaPoint;
  mapOptions?: google.maps.MapOptions;
  marker?: MappaPoint | null;
  markerOptions?: google.maps.MarkerOptions;
  markers?: MappaMarker[];
  polygons?: MappaPolygon[];
  polylines?: MappaPolyline[];
  showMarker?: boolean;
}

@Component({
  selector: 'formly-field-mappa',
  standalone: true,
  imports: [CommonModule, GoogleMap, MapMarker, MapPolygon, MapPolyline],
  template: `
    <google-map
      [height]="mapHeight()"
      [width]="mapWidth()"
      [zoom]="mapZoom()"
      [center]="mapCenter()"
      [options]="mapOptions()"
      class="block rounded-[var(--p-card-border-radius)] overflow-hidden"
    >
      @if (showMarker()) {
        <map-marker
          [position]="markerPosition()!"
          [options]="markerOptions()"
        ></map-marker>
      }

      @for (marker of props.markers ?? []; track $index) {
        <map-marker
          [position]="marker.position"
          [options]="extraMarkerOptions(marker)"
        ></map-marker>
      }

      @for (polygon of props.polygons ?? []; track $index) {
        <map-polygon
          [paths]="polygon.paths"
          [options]="polygonOptions(polygon)"
        ></map-polygon>
      }

      @for (polyline of props.polylines ?? []; track $index) {
        <map-polyline
          [path]="polyline.path"
          [options]="polylineOptions(polyline)"
        ></map-polyline>
      }
    </google-map>
  `,
})
export class MappaFieldType extends FieldType<FieldTypeConfig<MappaProps>> {
  private readonly defaultMapOptions: google.maps.MapOptions = {};
  private readonly defaultMarkerOptions: google.maps.MarkerOptions = {};
  private readonly defaultPolygonOptions: google.maps.PolygonOptions = {};
  private readonly defaultPolylineOptions: google.maps.PolylineOptions = {};

  mapHeight = computed(() => this.props.height ?? '350px');
  mapWidth = computed(() => this.props.width ?? '100%');
  mapZoom = computed(() => this.props.zoom ?? 12);
  mapCenter = computed<MappaPoint>(() => this.props.center ?? { lat: 41.9028, lng: 12.4964 });
  mapOptions = computed<google.maps.MapOptions>(
    () => this.props.mapOptions ?? this.defaultMapOptions,
  );

  markerPosition = computed<MappaPoint | null>(() => this.props.marker ?? null);
  markerOptions = computed<google.maps.MarkerOptions>(
    () => this.props.markerOptions ?? this.defaultMarkerOptions,
  );

  showMarker = computed(() => {
    if (this.props.showMarker === false) return false;
    return !!this.markerPosition();
  });

  polygonOptions(polygon: MappaPolygon): google.maps.PolygonOptions {
    return polygon.options ?? this.defaultPolygonOptions;
  }

  polylineOptions(polyline: MappaPolyline): google.maps.PolylineOptions {
    return polyline.options ?? this.defaultPolylineOptions;
  }

  extraMarkerOptions(marker: MappaMarker): google.maps.MarkerOptions {
    return marker.options ?? this.defaultMarkerOptions;
  }
}
